import type { Database } from "../database.types";

export type DocumentType = Database["public"]["Enums"]["document_type"];

/**
 * Determines the storage bucket for a given document type.
 * Mirrors the logic in the database function `get_bucket_for_document_type`.
 */
export function getBucketForDocumentType(documentType: string): string {
  const type = documentType as string;

  // ID Verification (HIGH sensitivity)
  if (["DRIVERS_LICENSE", "PASSPORT", "PROOF_OF_AGE_CARD"].includes(type)) {
    return "id-verification";
  }

  // Insurance Documents (HIGH sensitivity)
  if (
    [
      "INSURANCE_CERTIFICATE",
      "PUBLIC_LIABILITY_INSURANCE",
      "PROFESSIONAL_INDEMNITY_INSURANCE",
      "WORKERS_COMPENSATION_INSURANCE",
    ].includes(type)
  ) {
    return "insurance-documents";
  }

  // Trade Licenses (MEDIUM sensitivity)
  if (
    [
      "TRADE_LICENSE",
      "TRADE_LICENSE_ELECTRICAL",
      "TRADE_LICENSE_PLUMBING",
      "TRADE_LICENSE_BUILDING",
      "TRADE_LICENSE_GAS",
      "TRADE_LICENSE_HVAC",
      "TRADE_LICENSE_OTHER",
    ].includes(type)
  ) {
    return "trade-licenses";
  }

  // Compliance Certificates (MEDIUM sensitivity)
  if (
    [
      "WHITE_CARD",
      "WORKING_AT_HEIGHTS",
      "CONFINED_SPACE",
      "ASBESTOS_AWARENESS",
      "FIRST_AID_CERTIFICATE",
    ].includes(type)
  ) {
    return "compliance-certificates";
  }

  // Business Documents (MEDIUM sensitivity)
  if (
    ["BUSINESS_REGISTRATION", "TAX_FILE_NUMBER", "BANK_STATEMENT"].includes(
      type,
    )
  ) {
    return "business-documents";
  }

  // General Documents (LOW sensitivity)
  if (["PROFILE_PHOTO", "GENERAL"].includes(type)) {
    return "general-documents";
  }

  // Default fallback
  return "general-documents";
}

/**
 * Gets the storage bucket for a document record.
 * Uses the `bucket_id` field if present, otherwise falls back to 'trade-documents'
 * for backward compatibility with existing records.
 */
export function getDocumentBucket(
  document: {
    bucket_id?: string | null;
    document_type?: DocumentType | string | null;
  },
): string {
  if (document.bucket_id) {
    return document.bucket_id;
  }

  // Default to legacy bucket for older records
  return "trade-documents";
}
